import { PermissionActionEnum, type ViewFieldsFragment } from '@salescore/client-api'
import { useMe } from '@salescore/client-common'
import { CORE_CONSTANT, type ViewConfigKpi } from '@salescore/core'
import { ToggleText } from '@salescore/frontend-common'
import { Empty } from 'antd'
import { t } from 'i18next'
import type { ReactElement } from 'react'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'

import {
  kpiFormAtom,
  kpiFormModifiedAtom,
  type SalescoreObjectAbility,
  type ViewAbility,
  viewsContextAtom,
} from '../../../../recoil/view/atoms'
import { attachMetricToKpiConfigFromInline } from '../../../../recoil/view/attachMetricToKpiConfig'
import { useSetSheetConfig, useValidatedFormValue } from '../../../../recoil/view/hooks/kpiForm'
import { useCurrentSheet } from '../../../../recoil/view/selectors/kpiFormSelector'
import { DurationTimerStart } from '../../../misc/DurationTimer'
import { ViewComponent } from '../../../ViewComponent'

const dynamicViewFields: Omit<ViewFieldsFragment, 'id' | 'ui' | 'queries' | 'config'> = {
  viewGroupId: ``,
  type: `sheet`,
  name: ``,
  rank: 0,
  defaultPermission: PermissionActionEnum.None,
  importedSourceId: ``,
  emoji: ``,
  roleRecordPermissions: [],
  private: false,
  archived: false,
  archivedAt: null,
}

export const KpiFormPreview = ({
  view,
  ability,
}: {
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  const { organization } = useMe()
  const { kpiConfig } = useValidatedFormValue()
  const [formModified, setFormModified] = useRecoilState(kpiFormModifiedAtom)
  const currentSheet = useCurrentSheet()
  const setSheetConfig = useSetSheetConfig()
  const setForm = useSetRecoilState(kpiFormAtom)
  const viewsContext = useRecoilValue(viewsContextAtom)

  if (!kpiConfig.success) {
    return (
      <Empty
        description={
          <div>
            {t(`KPI設定が終わっていません。`)}
            <br />
            <ToggleText text={t(`詳細`)}>
              <>{kpiConfig.error.message}</>
            </ToggleText>
          </div>
        }
      />
    )
  }

  // compile は formula.metric のみを参照するため、inline (sheet/measure/date/user) を
  // formula に再 derive してから渡す。inline 不足時は formula.type === 'empty' で返るので、
  // その場合も Empty UI に流す。
  const config = attachMetricToKpiConfigFromInline(kpiConfig.data, view.id)
  if (config.formula.type === 'empty') {
    return <Empty description={<div>{t(`KPI設定が終わっていません。`)}</div>} />
  }

  const previewViewId = `${CORE_CONSTANT.KPI_PREVIEW_VIEW_ID_PREFIX}${view.id}`
  // configの変更があった場合にKPIプレビューを再レンダリングさせるために、configを元にキーを生成している
  const kpiPreviewKey = generateKpiPreviewKey({ config })

  return (
    <>
      <ViewComponent
        key={kpiPreviewKey}
        view={{
          ...dynamicViewFields,
          id: previewViewId,
          config,
        }}
        parentAbility={ability}
        parameter={{}}
        // onFinish={onFinish}
        viewsContext={{
          ...viewsContext,
          parentViewProtectingUnload: formModified,
          // eslint-disable-next-line @typescript-eslint/require-await
          updateView: async (argument): Promise<undefined> => {
            if (argument.config?.type === 'sheet') {
              // プレビューモーダルの中のシートから `updateView` が呼ばれた場合の処理
              const sheetViewConfig = argument.config
              const newSheet = {
                ...currentSheet,
                tree: sheetViewConfig.tree,
                fields: sheetViewConfig.fields,
                filterTree: sheetViewConfig.filterTree,
                sorters: sheetViewConfig.sorters,
                meta: {
                  fixedLeftColumnIndex: sheetViewConfig.meta?.fixedLeftColumnIndex,
                  visibleRowNum: sheetViewConfig.meta?.visibleRowNum,
                },
              }
              setSheetConfig(newSheet)
              // フィルター条件を変更した時に、ViewFilterTreeFormのキャッシュを更新するために、キャッシュキーを更新する
              // キャッシュキーが書き換わればなんでも良い（ので、ここでは現在時刻をキャッシュキーにしている）
              setForm((oldValue) => ({ ...oldValue, filterFormItemCacheKey: new Date().toISOString() }))
              setFormModified(true)
            }
            // eslint-disable-next-line sonarjs/todo-tag
            // TODO: arg としてシートの config 以外が渡された場合（特に KPI の config が渡された場合）の対応が必要になるかもしれない
            // if (arg.config?.type === 'kpi') {
            //   const kpiViewConfig = arg.config
            //   setKpi(kpiViewConfig)
            //   setFormModified(true)
            //   return undefined
            // }
          },
        }}
      />
      <DurationTimerStart
        // kpiPreviewKeyが切り替わる度にdurationの計測をトリガーする
        key={`${kpiPreviewKey}_duration`}
        trigger="kpiPreviewUpdated"
        viewId={previewViewId}
        organizationId={organization.id}
      />
    </>
  )
}

function generateKpiPreviewKey({ config }: { config: ViewConfigKpi }) {
  const ui = {
    ...config.ui,
    kpi: {
      ...config.ui?.kpi,
      // アイコンと説明文を変更してもKPIプレビューの再レンダリングが発生しないようにする
      icon: undefined,
      description: undefined,
    },
  }

  return JSON.stringify({
    ...config,

    ui,
  })
}
